import { FieldController } from "@/components/UI/forms/FieldController";
import { RequiredAstrisk } from "@/components/UI/forms/RequiredAstrisk";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Checkbox,
  Heading,
  Link,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { Control } from "react-hook-form";
import { RegistryFormData } from "..";
import { LocalizedLink } from "@/components/i18n/LocalizedLink";
import { useTranslation, Trans } from "react-i18next";

// Reusable link components for Trans
const PrivacyLink = (props: React.PropsWithChildren) => (
  <Text
    as="a"
    textDecor="underline"
    fontWeight="medium"
    color="blue.500"
    href="https://science.xyz/legal/privacy"
    target="_blank"
    rel="noopener"
    {...props}
  />
);

const CookieLink = (props: React.PropsWithChildren) => (
  <Text
    as="a"
    textDecor="underline"
    fontWeight="medium"
    color="blue.500"
    href="https://science.xyz/legal/cookies/"
    target="_blank"
    rel="noopener"
    {...props}
  />
);

const ChdpLink = (props: React.PropsWithChildren) => (
  <Text
    as="a"
    textDecor="underline"
    fontWeight="medium"
    color="blue.500"
    href="https://science.xyz/legal/chdp-policy/"
    target="_blank"
    rel="noopener"
    {...props}
  />
);

const GoogleLink = (props: React.PropsWithChildren) => (
  <Text
    as="a"
    textDecor="underline"
    fontWeight="medium"
    color="blue.500"
    href="https://policies.google.com/privacy"
    target="_blank"
    rel="noopener"
    {...props}
  />
);

const ComplianceLink = (props: React.PropsWithChildren) => (
  <Text
    as="a"
    textDecor="underline"
    fontWeight="medium"
    color="blue.500"
    href="https://science.xyz/legal/privacy/#compliance-and-protection"
    target="_blank"
    rel="noopener"
    {...props}
  />
);

const EmailLink = ({ email, ...props }: React.PropsWithChildren<{ email: string }>) => (
  <Text
    as="a"
    textDecor="underline"
    fontWeight="medium"
    color="blue.500"
    href={`mailto:${email}`}
    target="_blank"
    rel="noopener"
    {...props}
  />
);

export const ConsentForm = (props: {
  consentPreviouslyGranted: boolean;
  isEditable?: boolean;
  formControl: Control<RegistryFormData>;
}) => {
  const { consentPreviouslyGranted, isEditable = true, formControl } = props;
  const isDisabled = consentPreviouslyGranted || !isEditable;
  const { t } = useTranslation(["registry", "validation", "common"]);

  const supportEmail = t("common:emails.support");
  const privacyEmail = t("common:emails.privacy");

  return (
    <VStack
      paddingBlock={[0, 4, 8]}
      spacing={6}
      maxWidth="container.md"
      margin="auto"
      alignItems="flex-start"
      marginBlockEnd={8}
      fontSize="lg"
      sx={{ "& [data-disabled]": { opacity: "1 !important" } }}
    >
      <Heading fontSize={["2xl", "3xl", "4xl"]} fontWeight="regular" marginBlockEnd={4}>
        <Text
          fontSize="md"
          as="div"
          textTransform="uppercase"
          color="gray.600"
          fontWeight="semibold"
        >
          {t("registry:consent.stepLabel")}
        </Text>
        {t("registry:consent.heading")}
      </Heading>

      {!isEditable && !consentPreviouslyGranted && (
        <Alert status="info" fontSize="lg" padding={6} backgroundColor="gray.50" borderRadius="md">
          <AlertIcon boxSize={6} marginInlineStart={2} marginInlineEnd={6} />
          <AlertDescription>
            <Trans
              i18nKey="registry:consent.completeGeneralFirst"
              components={{
                generalLink: (
                  <Link
                    as={LocalizedLink}
                    color="blue.500"
                    textDecoration="underline"
                    fontWeight="medium"
                    to="/registry"
                  />
                ),
              }}
            />
          </AlertDescription>
        </Alert>
      )}

      {consentPreviouslyGranted && (
        <Alert
          padding={6}
          borderRadius="md"
          backgroundColor="gray.50"
          borderColor="gray.200"
          borderWidth="1px"
          status="info"
          fontSize="lg"
        >
          <AlertIcon boxSize={6} marginInlineStart={2} marginInlineEnd={6} />
          <AlertDescription>
            <Trans
              i18nKey="registry:consent.lockedAlert"
              values={{ email: supportEmail }}
              components={{
                email: <EmailLink email={supportEmail} />,
              }}
            />
          </AlertDescription>
        </Alert>
      )}

      <Text>
        <Trans i18nKey="registry:consent.intro1" components={{ strong: <strong /> }} />
      </Text>

      <Text>
        <Trans
          i18nKey="registry:consent.intro2"
          components={{
            underline: <span style={{ fontWeight: "bold", textDecoration: "underline" }} />,
          }}
        />
      </Text>

      <Text>{t("registry:consent.intro3")}</Text>

      <Text>
        <Trans
          i18nKey="registry:consent.intro4"
          values={{ email: privacyEmail }}
          components={{ email: <EmailLink email={privacyEmail} /> }}
        />
      </Text>

      <Text>
        <Trans
          i18nKey="registry:consent.controllerInfo"
          values={{ email: privacyEmail }}
          components={{ email: <EmailLink email={privacyEmail} /> }}
        />
      </Text>

      <Heading marginBlockStart={6} size="lg" fontWeight="medium" lineHeight="shorter">
        {t("registry:consent.noticeHeading")}
      </Heading>

      <Text>
        <Trans
          i18nKey="registry:consent.noticeIntro"
          components={{
            privacyLink: <PrivacyLink />,
            cookieLink: <CookieLink />,
          }}
        />
      </Text>

      <Stack marginInlineStart={8} as="ul" gap={2} fontSize="lg">
        <Box as="li">
          <Trans
            i18nKey="registry:consent.infoWeCollect"
            components={{
              strong: <strong />,
              chdpLink: <ChdpLink />,
              cookieLink: <CookieLink />,
            }}
          />
        </Box>
        <Box as="li">
          <Trans
            i18nKey="registry:consent.howWeUse"
            components={{
              strong: <strong />,
              privacyLink: <PrivacyLink />,
            }}
          />
        </Box>
        <Box as="li">
          <Trans
            i18nKey="registry:consent.yourRights"
            values={{ email: privacyEmail }}
            components={{
              strong: <strong />,
              privacyLink: <PrivacyLink />,
              email: <EmailLink email={privacyEmail} />,
            }}
          />
        </Box>
        <Box as="li">
          <Trans i18nKey="registry:consent.basedInUS" components={{ strong: <strong /> }} />
        </Box>
      </Stack>

      <Heading size="lg" fontWeight="medium">
        {t("registry:consent.consentHeading")}
      </Heading>

      <Text>{t("registry:consent.consentIntro")}</Text>

      <Stack marginInlineStart={8} as="ul" gap={2} fontSize="lg">
        <Box as="li">{t("registry:consent.certify1")}</Box>
        <Box as="li">{t("registry:consent.certify2")}</Box>
        <Box as="li">{t("registry:consent.certify3")}</Box>
        <Box as="li">
          <Trans
            i18nKey="registry:consent.certify4"
            components={{
              strong: <strong />,
              privacyLink: <PrivacyLink />,
            }}
          />
        </Box>
      </Stack>

      <Text>{t("registry:consent.confirmIntro")}</Text>

      <FieldController
        name="application.consent_health_collection"
        control={formControl}
        disabled={isDisabled}
        rules={{
          required: {
            value: true,
            message: t("validation:consent.healthCollectionRequired"),
          },
        }}
        hideRequiredAstrisk
        render={({ field }) => (
          <Checkbox
            data-testid={field.name}
            name={field.name}
            borderColor="gray.500"
            size="md"
            isChecked={field.value || false}
            isDisabled={field.disabled}
            onChange={(e) => {
              field.onChange(e.target.checked === true);
            }}
            style={{
              alignItems: "flex-start",
            }}
          >
            <Text position="relative" top={-1.5} fontSize="lg">
              <Trans
                i18nKey="registry:consent.consentHealthCollection"
                components={{ privacyLink: <PrivacyLink /> }}
              />
              <RequiredAstrisk />
            </Text>
          </Checkbox>
        )}
      />

      <FieldController
        name="application.consent_contact"
        control={formControl}
        disabled={isDisabled}
        rules={{
          required: {
            value: true,
            message: t("validation:consent.contactRequired"),
          },
        }}
        hideRequiredAstrisk
        render={({ field }) => (
          <Checkbox
            data-testid={field.name}
            name={field.name}
            borderColor="gray.500"
            size="md"
            isChecked={field.value || false}
            isDisabled={field.disabled}
            onChange={(e) => {
              field.onChange(e.target.checked === true);
            }}
            style={{
              alignItems: "flex-start",
            }}
          >
            <Text position="relative" top={-1.5} fontSize="lg">
              <Trans
                i18nKey="registry:consent.consentContact"
                components={{ privacyLink: <PrivacyLink /> }}
              />
              <RequiredAstrisk />
            </Text>
          </Checkbox>
        )}
      />

      <Stack paddingInlineStart={16}>
        <FieldController
          name="application.consent_contact_email"
          control={formControl}
          disabled={isDisabled}
          rules={{
            required: {
              value: true,
              message: t("validation:consent.emailRequired"),
            },
          }}
          hideRequiredAstrisk
          render={({ field }) => (
            <Checkbox
              data-testid={field.name}
              name={field.name}
              borderColor="gray.500"
              size="md"
              isChecked={field.value === true}
              isDisabled={field.disabled}
              onChange={(e) => {
                field.onChange(e.target.checked === true);
              }}
              style={{
                alignItems: "flex-start",
              }}
            >
              <Text position="relative" top={-1.5} fontSize="lg">
                {t("registry:consent.consentEmail")}
                <RequiredAstrisk />
              </Text>
            </Checkbox>
          )}
        />
        <FieldController
          name="application.consent_contact_phone"
          control={formControl}
          disabled={isDisabled}
          rules={{
            required: {
              value: false,
              message: t("validation:consent.phoneRequired"),
            },
          }}
          hideRequiredAstrisk
          render={({ field }) => (
            <Checkbox
              data-testid={field.name}
              name={field.name}
              borderColor="gray.500"
              size="md"
              isChecked={field.value === true}
              isDisabled={field.disabled}
              onChange={(e) => {
                field.onChange(e.target.checked === true);
              }}
              style={{
                alignItems: "flex-start",
              }}
            >
              <Text position="relative" top={-1.5} fontSize="lg">
                {t("registry:consent.consentPhone")}
              </Text>
            </Checkbox>
          )}
        />
      </Stack>

      <FieldController
        name="application.consent_contact_recurring"
        control={formControl}
        disabled={isDisabled}
        rules={{
          required: {
            value: false,
            message: t("validation:consent.recurringRequired"),
          },
        }}
        hideRequiredAstrisk
        render={({ field }) => (
          <Checkbox
            data-testid={field.name}
            name={field.name}
            borderColor="gray.500"
            size="md"
            isChecked={field.value === true}
            isDisabled={field.disabled}
            onChange={(e) => {
              field.onChange(e.target.checked === true);
            }}
            style={{
              alignItems: "flex-start",
            }}
          >
            {/* THIS FIELD IS OPTIONAL */}
            <Text position="relative" top={-1.5} fontSize="lg">
              {t("registry:consent.consentRecurring")}
            </Text>
          </Checkbox>
        )}
      />

      <FieldController
        name="application.consent_health_sharing"
        control={formControl}
        disabled={isDisabled}
        rules={{
          required: {
            value: true,
            message: t("validation:consent.sharingRequired"),
          },
        }}
        hideRequiredAstrisk
        render={({ field }) => (
          <Checkbox
            data-testid={field.name}
            name={field.name}
            borderColor="gray.500"
            size="md"
            isDisabled={field.disabled}
            style={{
              alignItems: "flex-start",
            }}
            isChecked={field.value === true}
            onChange={(e) => {
              field.onChange(e.target.checked === true);
            }}
          >
            <Text position="relative" top={-1.5} fontSize="lg">
              {t("registry:consent.consentSharing")}
              <RequiredAstrisk />
            </Text>
          </Checkbox>
        )}
      />

      <Stack marginInlineStart={[6, 10]} as="ul" gap={2} fontSize="lg">
        <Box as="li">
          <Trans i18nKey="registry:consent.serviceProviders" components={{ strong: <strong /> }} />
        </Box>
        <Box as="li">
          <Trans i18nKey="registry:consent.affiliates" components={{ strong: <strong /> }} />
        </Box>
        <Box as="li">
          <Trans
            i18nKey="registry:consent.authServices"
            components={{
              strong: <strong />,
              googleLink: <GoogleLink />,
            }}
          />
        </Box>
        <Box as="li">
          <Trans
            i18nKey="registry:consent.professionalAdvisors"
            components={{ strong: <strong /> }}
          />
        </Box>
        <Box as="li">
          <Trans
            i18nKey="registry:consent.authorities"
            components={{
              strong: <strong />,
              complianceLink: <ComplianceLink />,
            }}
          />
        </Box>
        <Box as="li">
          <Trans
            i18nKey="registry:consent.businessTransferees"
            components={{ strong: <strong /> }}
          />
        </Box>
        <Box as="li">
          <Trans i18nKey="registry:consent.otherParties" components={{ strong: <strong /> }} />
        </Box>
      </Stack>

      <Text>
        <Trans
          i18nKey="registry:consent.withdrawConsent"
          values={{ email: privacyEmail }}
          components={{ email: <EmailLink email={privacyEmail} /> }}
        />
      </Text>
    </VStack>
  );
};
